import { v4 } from 'uuid'

import { createSelector } from '@app/utils/createSelector'
import { captureException } from '@app/utils/errorReport/errorReport'
import { invariant } from '@app/utils/invariant'
import { assertTimezoneValid } from '@app/utils/moment'
import { placeLabel } from '@app/utils/placeLabel'
import { matchRegion, placeCookie, regionCookie } from '@app/utils/routing/region'

import { isNetworkError } from '@app/packages/api/errors'
import { createAddress } from '@app/packages/geo/Address'
import { createPromise } from '@app/packages/thunkSelector/promiseState'
import { createLoadable, mapThunkSelector } from '@app/packages/thunkSelector/thunkSelector'
import type { SliceState } from '@app/packages/thunkSelector/withCheckedSliceState'
import { withCheckedSliceState } from '@app/packages/thunkSelector/withCheckedSliceState'

import { callV2Api } from '@app/store/actions/api'
import { getCookies } from '@app/store/actions/contexts/cookies'
import { createReduxSlice } from '@app/store/redux_slice'
import { profileUserResultSelector, profileUserTimezoneStringSelector } from '@app/store/selectors/profile'
import { availableRegionsSlugsSelector, defaultRegionSelector } from '@app/store/selectors/regions'

import { countriesSelector, localitiesSelector, placesSelector } from './entities'
import { regionsMapSelector } from './regions'

const hashSlice = createReduxSlice<string>('profile_place_hash')
export const cookiePlaceIdSlice = createReduxSlice<SliceState<string | null>>('cookie_place_id')
const cookieRegionIdSlice = createReduxSlice<SliceState<string | null>>('cookie_region_id')

export const syncCookiePlaceIdSlice = () => hashSlice.set(v4())

const cookiePlaceIdResultSelector = createSelector([cookiePlaceIdSlice.selector, hashSlice.selector], (slice, hash) =>
  createLoadable(c => {
    return withCheckedSliceState(c, {
      sliceState: slice,
      slice: cookiePlaceIdSlice,
      hash,
      load: async (dispatch, getState) => {
        const { getPlacesById } = await import('@app/packages/api/v2/actions/places')
        const cookies = dispatch(getCookies())
        const placeId = await placeCookie.get(cookies)
        if (!placeId) return null
        const place = placesSelector(getState())[placeId]
        if (!place) {
          try {
            await dispatch(callV2Api(getPlacesById(placeId)))
          } catch (e) {
            if (isNetworkError(e) && e.status === 404) {
              await placeCookie.rm(cookies)
              dispatch(syncCookiePlaceIdSlice())
              return null
            }
          }
        }
        return placeId
      },
    })
  })
)

const regionPlaceIdResultSelector = createSelector([cookieRegionIdSlice.selector, hashSlice.selector], (slice, hash) =>
  createLoadable(c => {
    return withCheckedSliceState(c, {
      sliceState: slice,
      slice: cookieRegionIdSlice,
      hash,
      load: async (dispatch, getState) => {
        const { getRegionsById } = await import('@app/packages/api/v2/actions/regions')
        const cookies = dispatch(getCookies())
        const regionId = await regionCookie.get(cookies)
        if (!regionId) return null
        let region = regionsMapSelector(getState())[regionId]
        if (region) {
          try {
            region = (await dispatch(callV2Api(getRegionsById(regionId)))).data
          } catch (e) {
            if (isNetworkError(e) && e.status === 404) {
              await regionCookie.rm(cookies)
              dispatch(syncCookiePlaceIdSlice())
              return null
            }
          }
        }
        return region.relationships.default_place.data?.id ?? invariant('Region missing a default place')
      },
    })
  })
)

export const defaultPlaceIdResultSelector = createSelector([defaultRegionSelector], region =>
  createLoadable(_c => {
    return region.relationships.default_place.data?.id ?? invariant('Default region missing a default place')
  })
)

/** The place the visitor has said something about — profile, place cookie or region cookie. `null` before they have. */
export const chosenPlaceIdResultSelector = createSelector(
  [profileUserResultSelector, cookiePlaceIdResultSelector, regionPlaceIdResultSelector],
  (userResult, cookiePlaceIdResult, regionPlaceIdResult) =>
    createLoadable(c => {
      const user = c.unwrap(userResult)
      if (user && 'place_id' in user && user.place_id) {
        return String(user.place_id)
      }

      const cookiePlaceId = c.unwrap(cookiePlaceIdResult)
      if (cookiePlaceId) return cookiePlaceId

      const regionPlaceId = c.unwrap(regionPlaceIdResult)
      if (regionPlaceId) return regionPlaceId

      return null
    })
)

export const profilePlaceIdResultSelector = createSelector(
  [chosenPlaceIdResultSelector, defaultPlaceIdResultSelector],
  (chosenPlaceIdResult, defaultPlaceIdResult) =>
    createLoadable(c => {
      const chosenPlaceId = c.unwrap(chosenPlaceIdResult)
      if (chosenPlaceId) return chosenPlaceId

      return c.unwrap(defaultPlaceIdResult)
    })
)

export const profilePlaceResultSelector = createSelector([profilePlaceIdResultSelector, placesSelector], (placeIdResult, places) =>
  createLoadable(c => {
    const placeId = c.unwrap(placeIdResult)
    const place = places[placeId]
    if (!place) {
      return c.load(
        createPromise('Profile place must be loaded', placeId, async (dispatch, getState) => {
          const { getPlacesById } = await import('@app/packages/api/v2/actions/places')
          await dispatch(callV2Api(getPlacesById(placeId)))
          const place = placesSelector(getState())[placeId]
          if (!place) throw new Error('Place not found after fetch')
        })
      )
    }
    return place
  })
)

export const profilePlaceBundleResultSelector = createSelector(
  [profilePlaceResultSelector, regionsMapSelector, countriesSelector, localitiesSelector],
  (placeResult, regions, countries, localities) =>
    createLoadable(c => {
      const place = c.unwrap(placeResult)
      const region = regions[place.relationships.region.data?.id ?? invariant('Place missing region')]
      const country = (place.relationships.country?.data?.id && countries[place.relationships.country.data.id]) || null
      const locality = (place.relationships.locality?.data?.id && localities[place.relationships.locality.data.id]) || null
      return { place, region, country, locality }
    })
)

export const profilePlaceNameResultSelector = mapThunkSelector(profilePlaceBundleResultSelector, data => placeLabel(data))
export const profileRegionResultSelector = mapThunkSelector(profilePlaceBundleResultSelector, data => data.region)
export const profileRegionIdResultSelector = mapThunkSelector(profilePlaceBundleResultSelector, data => data.region.id)
/** The region of the place the visitor has said something about; `null` before they have, rather than the default's. */
export const chosenRegionIdResultSelector = createSelector(
  [chosenPlaceIdResultSelector, profileRegionIdResultSelector],
  (chosenPlaceIdResult, regionIdResult) => createLoadable(c => (c.unwrap(chosenPlaceIdResult) === null ? null : c.unwrap(regionIdResult)))
)
export const profileRegionSlugResultSelector = mapThunkSelector(profilePlaceBundleResultSelector, data => data.region.attributes.slug)
export const profileRegionWebSlugResultSelector = createSelector([availableRegionsSlugsSelector, profileRegionSlugResultSelector], (slugs, regionSlugResult) =>
  createLoadable(c => {
    const regionSlug = c.unwrap(regionSlugResult)
    return matchRegion(slugs, regionSlug)
  })
)

export const profilePlaceAddressLocationResultSelector = createSelector(
  [profilePlaceNameResultSelector, profilePlaceResultSelector],
  (nameResult, placeResult) =>
    createLoadable(c => {
      const name = c.unwrap(nameResult)
      const place = c.unwrap(placeResult)
      return createAddress({
        label: name,
        location: { lat: place.attributes.latitude, lon: place.attributes.longitude },
      })
    })
)

/** Where the chosen place is — `null` while only the default region stands in for one. */
export const chosenPlaceLocationResultSelector = createSelector([chosenPlaceIdResultSelector, profilePlaceResultSelector], (chosenPlaceIdResult, placeResult) =>
  createLoadable(c => {
    if (!c.unwrap(chosenPlaceIdResult)) return null
    const place = c.unwrap(placeResult)
    return { lat: place.attributes.latitude, lon: place.attributes.longitude }
  })
)

export const profilePlaceInputValueResultSelector = profilePlaceBundleResultSelector

export const profilePlaceTimezoneResultSelector = createSelector(
  [profileUserTimezoneStringSelector, profileRegionResultSelector],
  (profileTimezone, regionResult) =>
    createLoadable(c => {
      const tz = (() => {
        if (profileTimezone) return profileTimezone
        if (IS_BROWSER) return Intl.DateTimeFormat().resolvedOptions().timeZone

        return 'Europe/Moscow'
      })()

      try {
        assertTimezoneValid(tz)
      } catch (e) {
        captureException(e)
        const region = c.unwrap(regionResult)
        return region.attributes.timezone
      }
      return tz
    })
)
